import type {ImageProcessingNodes} from "@cm/image-processing-nodes"
import {assertNever} from "@cm/utils"
import {wrap} from "@cm/math"

// Precomputed Lanczos weight table factory.
// Avoids Math.sin calls in the inner rasterization loop.
function buildLanczosWeightTable(a: number, tableSize: number): Float32Array {
    const table = new Float32Array(tableSize * 2 * a)
    for (let t = 0; t < tableSize; t++) {
        const frac = t / tableSize
        for (let i = 1 - a; i <= a; i++) {
            const x = i - frac
            let w: number
            if (x === 0) {
                w = 1
            } else if (Math.abs(x) >= a) {
                w = 0
            } else {
                const px = Math.PI * x
                w = (a * Math.sin(px) * Math.sin(px / a)) / (px * px)
            }
            table[t * 2 * a + (i - (1 - a))] = w
        }
    }
    return table
}

// Precomputed Lanczos3 weight table: 256 sub-pixel positions × 6 taps.
const lanczos3TableSize = 256
const lanczos3WeightTable = buildLanczosWeightTable(3, lanczos3TableSize)

// Precomputed Lanczos4 weight table: 256 sub-pixel positions × 8 taps.
const lanczos4TableSize = 256
const lanczos4WeightTable = buildLanczosWeightTable(4, lanczos4TableSize)

// Creates a reusable sampler function for a Float32 image buffer.
// The returned function writes numChannels values into out[outOffset..outOffset+numChannels].
// Coordinates (x, y) must be in top-left-aligned pixel space (center-pixel origin already shifted by -0.5).
export function createFloat32ImageSampler(
    data: Float32Array,
    width: number,
    height: number,
    numChannels: number,
    addressMode: ImageProcessingNodes.PixelAddressMode,
    interpolationMode: ImageProcessingNodes.SamplerInterpolationMode,
): (x: number, y: number, out: Float32Array, outOffset: number) => void {
    const readTexel = (x: number, y: number, out: Float32Array, outOffset: number) => {
        switch (addressMode) {
            case "wrap":
                x = wrap(x, width)
                y = wrap(y, height)
                break
            case "clamp":
                x = Math.min(Math.max(0, x), width - 1)
                y = Math.min(Math.max(0, y), height - 1)
                break
            case "border":
                if (x < 0 || x >= width || y < 0 || y >= height) {
                    for (let c = 0; c < numChannels; c++) out[outOffset + c] = 0
                    return
                }
                break
            default:
                assertNever(addressMode)
        }
        const bufferIndex = (y * width + x) * numChannels
        for (let c = 0; c < numChannels; c++) out[outOffset + c] = data[bufferIndex + c]
    }
    if (interpolationMode === "nearest") {
        return (x, y, out, outOffset) => readTexel(Math.round(x), Math.round(y), out, outOffset)
    } else if (interpolationMode === "linear") {
        const tapBuf = new Float32Array(4 * numChannels)
        return (x, y, out, outOffset) => {
            const xFloor = Math.floor(x)
            const yFloor = Math.floor(y)
            const fx = x - xFloor
            const fy = y - yFloor
            const ifx = 1 - fx
            const ify = 1 - fy
            readTexel(xFloor, yFloor, tapBuf, 0 * numChannels)
            readTexel(xFloor + 1, yFloor, tapBuf, 1 * numChannels)
            readTexel(xFloor, yFloor + 1, tapBuf, 2 * numChannels)
            readTexel(xFloor + 1, yFloor + 1, tapBuf, 3 * numChannels)
            for (let c = 0; c < numChannels; c++) {
                out[outOffset + c] =
                    tapBuf[0 * numChannels + c] * ifx * ify +
                    tapBuf[1 * numChannels + c] * fx * ify +
                    tapBuf[2 * numChannels + c] * ifx * fy +
                    tapBuf[3 * numChannels + c] * fx * fy
            }
        }
    } else if (interpolationMode === "lanczos3") {
        const tapBuf = new Float32Array(numChannels)
        return (x, y, out, outOffset) => {
            const xFloor = Math.floor(x)
            const yFloor = Math.floor(y)
            const xFraction = x - xFloor
            const yFraction = y - yFloor
            const xTableOffset = (Math.round(xFraction * lanczos3TableSize) % lanczos3TableSize) * 6
            const yTableOffset = (Math.round(yFraction * lanczos3TableSize) % lanczos3TableSize) * 6
            for (let c = 0; c < numChannels; c++) out[outOffset + c] = 0
            for (let j = 0; j < 6; j++) {
                const wy = lanczos3WeightTable[yTableOffset + j]
                const sy = yFloor - 2 + j
                for (let i = 0; i < 6; i++) {
                    const w = lanczos3WeightTable[xTableOffset + i] * wy
                    readTexel(xFloor - 2 + i, sy, tapBuf, 0)
                    for (let c = 0; c < numChannels; c++) out[outOffset + c] += w * tapBuf[c]
                }
            }
        }
    } else if (interpolationMode === "lanczos4") {
        const tapBuf = new Float32Array(numChannels)
        return (x, y, out, outOffset) => {
            const xFloor = Math.floor(x)
            const yFloor = Math.floor(y)
            const xFraction = x - xFloor
            const yFraction = y - yFloor
            const xTableOffset = (Math.round(xFraction * lanczos4TableSize) % lanczos4TableSize) * 8
            const yTableOffset = (Math.round(yFraction * lanczos4TableSize) % lanczos4TableSize) * 8
            for (let c = 0; c < numChannels; c++) out[outOffset + c] = 0
            for (let j = 0; j < 8; j++) {
                const wy = lanczos4WeightTable[yTableOffset + j]
                const sy = yFloor - 3 + j
                for (let i = 0; i < 8; i++) {
                    const w = lanczos4WeightTable[xTableOffset + i] * wy
                    readTexel(xFloor - 3 + i, sy, tapBuf, 0)
                    for (let c = 0; c < numChannels; c++) out[outOffset + c] += w * tapBuf[c]
                }
            }
        }
    } else {
        throw new Error("Unsupported interpolation mode: " + interpolationMode)
    }
}
